import { useEffect, useRef, useState } from "react"
import EmptyFolder from 'assets/img/app/empty-folder.png';
import { IonDatetime, IonIcon, IonSpinner } from "@ionic/react";
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, FormControlLabel, Pagination } from "@mui/material";
import { ImageVisualizer, ModalBackground, SidebarMenu, SolidButton } from "components";
import { formatDateToISODateString, formatDateToLocaleDateString, formatISODateStringToDate } from "utils";
import { caretDown } from 'ionicons/icons';
import axios from 'axios';
import { useAppSelector } from 'hooks';


export const GondolaColdPage = () => {
    const today = new Date();
    const { token } = useAppSelector(state => state.user);

    // estados para almacenar los datos del reporte
    const [reports, setReports] = useState<any[]>([]);
    const [backupReports, setBackupReports] = useState<any[]>([]);

    // estados para manejar la paginación
    const [paginationCount, setPaginationCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const recordsPerView = 10

    // estados para manejar las pantallas de carga
    const [loading, setLoading] = useState<boolean>(false);

    // estado que almacena el inicio del rango de fechas de los benchmarks consultadas
    const [startDate, setStartDate] = useState<Date>(new Date((new Date()).setDate(today.getDate() - 30)));
    const [finishDate, setFinishDate] = useState<Date>(new Date());

    // estado para almacenar el arreglo de imágenes que se mostrarán en pantalla
    const [imagesToShow, setImagesToShow] = useState<string[]>([]);
    const [showImageModal, setShowImageModal] = useState<boolean>(false);

    // estados para almacenar los valores que aparecerán en los filtros de búsqueda
    const [searchFilters, setSearchFilters] = useState<{ user: any, sucursal: any }>({
        user: [],
        sucursal: []
    });

    const contentRef = useRef<HTMLDivElement>(null);

    const getBenchmarkDocs = async (signal: AbortSignal) => {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_SERVICE_URL}/benchmark/get/report`, {
            params: {
                startDate: `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`,
                endDate: `${finishDate.getFullYear()}-${(finishDate.getMonth() + 1).toString().padStart(2, '0')}-${finishDate.getDate().toString().padStart(2, '0')}`,
            },
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `UserToken ${token}`
            },
            signal: signal
        })

        const benchmarksData = response.data?.data?.gondolaAndCold || [];

        setPaginationCount(Math.ceil(benchmarksData.length / recordsPerView));
        setCurrentPage(1);
        setReports(benchmarksData)
        setBackupReports(benchmarksData)

        setSearchFilters(() => {
            let user = response.data?.data?.accounts || [];
            let sucursal = response.data?.data?.sucursals || [];
            user = user.map((account: any) => ({ ...account, checked: true }))
            sucursal = sucursal.map((suc: any) => ({ ...suc, checked: true }))
            return {
                sucursal: [{
                    key: 'all',
                    name: 'Todas las sucursales',
                    checked: true
                }, ...sucursal],
                user: [{
                    key: 'all',
                    name: 'Todos los displays',
                    lastName: '',
                    checked: true
                }, ...user]
            }
        })
        setLoading(false);
    }

    useEffect(() => {
        const controller = new AbortController();
        getBenchmarkDocs(controller.signal);
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changePaginationHandler = (event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    const changeSearchFilterHandler = (item: any, filterType: 'user' | 'sucursal') => {

        setSearchFilters((prevFilters) => {
            let updatedFilterItems = prevFilters[filterType].map((filterItem: any) => {
                if (filterItem.key === item.key) {
                    return { ...filterItem, checked: !filterItem.checked };
                }
                return filterItem;
            });

            if (item.key === 'all') {
                updatedFilterItems = updatedFilterItems.map((filterItem: any) => ({ ...filterItem, checked: !item.checked }));
                setReports(() => item.checked ? [] : backupReports);
            } else {
                setReports(() => {
                    const filteredReports = backupReports.filter((r: any) => {
                        const filterItem = updatedFilterItems.find((fi: any) => fi.key === r[filterType === 'user' ? 'displayKey' : 'sucursalId']);
                        return filterItem ? filterItem.checked : false;
                    });
                    return filteredReports;
                })
            }
            return {
                ...prevFilters,
                [filterType]: updatedFilterItems
            };
        })
    }

    const showImageHandler = (images: any[]) => {
        const imageUrls = images.map((img: any) => img.url);
        setImagesToShow(imageUrls);
        setShowImageModal(true);
    }

    return (
        <>
            <div ref={contentRef}>
                <div className="w-full h-screen grid grid-cols-12">
                    {/* Menú lateral */}
                    <div className="col-span-2 border-r-2 border-light">
                        <SidebarMenu pageName="gondola_cold" />
                    </div>

                    <div className="w-full h-screen flex flex-col col-span-10">
                        <div className="w-full flex justify-between border-b-2 border-tertiary px-2 py-3">
                            <p className="text-primary font-bold text-lg">Reporte de Góndola y Frío</p>

                            <p className="font-semibold text-lg">Del {formatDateToLocaleDateString(startDate)} al {formatDateToLocaleDateString(finishDate)}</p>
                        </div>

                        <div id="main-section" className="w-full h-screen overflow-y-scroll grid grid-cols-10">
                            {
                                loading ?
                                    <div className="col-span-10 w-full flex flex-col items-center justify-center">
                                        <IonSpinner name="crescent" color="primary" className="w-[50px] h-[50px]" />
                                    </div>
                                    :
                                    <div className="w-full flex flex-col flex-grow col-span-7">
                                        {
                                            loading ?
                                                <div className="w-full h-screen flex flex-col items-center justify-center">
                                                    <IonSpinner name="crescent" color="primary" className="w-[50px] h-[50px]" />
                                                </div>
                                                :
                                                reports.length === 0 ?
                                                    <div className="h-full grid grid-cols-8">
                                                        <div className="col-span-8 flex flex-col items-center gap-4 p-4">
                                                            <img src={EmptyFolder} alt='empty' className="h-[200px] object-contain" />

                                                            <p className="text-xl text-center font-semibold text-primary">No se encontraron resultados para tu búsqueda</p>
                                                        </div>
                                                    </div>
                                                    :
                                                    <div className="w-full h-full flex flex-col gap-2">
                                                        <div className="px-2 pt-4 pb-1 grid grid-cols-9 gap-4 text-mid border-b-2 border-tertiary [&>p]:mt-auto [&>p]:text-primary [&>p]:font-semibold">
                                                            <p>Display</p>
                                                            <p>Fecha de creación</p>
                                                            <p>Fecha de inicio</p>
                                                            <p>Fecha de finalización</p>
                                                            <p>Sucursal</p>
                                                            <p>Sección</p>
                                                            <p>Subsección</p>
                                                            <p>Imagen</p>
                                                            <p>Locación</p>
                                                        </div>

                                                        <div className="w-full flex flex-col px-2">
                                                            {
                                                                reports.map((item: any, i: number) => (
                                                                    i >= (recordsPerView * currentPage - recordsPerView) && i < (recordsPerView * currentPage) ?
                                                                        <Accordion key={item.key} defaultExpanded={false}>
                                                                            <AccordionSummary
                                                                                expandIcon={<IonIcon icon={caretDown} className="text-[25px] text-black" />}
                                                                                aria-controls="panel1a-content"
                                                                                id="panel1a-header"
                                                                                sx={{ backgroundColor: '#F4F5F5' }}
                                                                            >
                                                                                <div
                                                                                    className="w-full items-center grid grid-cols-9 gap-4 text-mid"
                                                                                >
                                                                                    <p>{item.display}</p>
                                                                                    <p>{item.created}</p>
                                                                                    <p>{item.createdComplete}</p>
                                                                                    <p>{item.finishedAt}</p>
                                                                                    <p>{item.sucursal}</p>

                                                                                </div>
                                                                            </AccordionSummary>

                                                                            <AccordionDetails>
                                                                                {
                                                                                    item.records?.map((record: any, index: number) => (
                                                                                        <div key={item.key + index} className='w-full bg-white cursor:pointer [&>div]:text-ellipsis [&>div]:overflow-hidden [&>div]:... [&>div]:p-2 text-sm text-gray-700 grid grid-cols-9'>
                                                                                            <div> {record.name} </div>
                                                                                            <div> {record.created} </div>
                                                                                            <div> {record.createdComplete} </div>
                                                                                            <div> {record.finishedAt} </div>
                                                                                            <div> {record.sucursal} </div>
                                                                                            <div> {record.section} </div>
                                                                                            <div> {record.subsection} </div>

                                                                                            <div>
                                                                                                {record.images.length > 0 ? (
                                                                                                    <button
                                                                                                        className="bg-tertiary hover:opacity-80 font-semibold px-2 py-1 rounded-lg text-small"
                                                                                                        onClick={() => showImageHandler(record.images)}
                                                                                                    >
                                                                                                        {record.images.length > 1 ? "Ver imágenes" : "Ver imagen"}
                                                                                                    </button>
                                                                                                ) : (
                                                                                                    <div style={{ height: "36px" }}></div>
                                                                                                )}
                                                                                            </div>
                                                                                            <div className="text-center">
                                                                                                {record.showModalValidate ? (
                                                                                                    <div
                                                                                                        style={{
                                                                                                            width: "20px",
                                                                                                            height: "20px",
                                                                                                            backgroundColor: "#28a745", // Verde para true
                                                                                                            borderRadius: "50%",
                                                                                                            display: "inline-block",
                                                                                                        }}
                                                                                                    ></div>
                                                                                                ) : (
                                                                                                    <div
                                                                                                        style={{
                                                                                                            width: "20px",
                                                                                                            height: "20px",
                                                                                                            backgroundColor: "#dc3545", // Rojo para false
                                                                                                            borderRadius: "50%",
                                                                                                            display: "inline-block",
                                                                                                        }}
                                                                                                    ></div>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    ))
                                                                                }
                                                                            </AccordionDetails>
                                                                        </Accordion>
                                                                        :
                                                                        null
                                                                ))
                                                            }
                                                        </div>
                                                    </div>
                                        }

                                        <div className={`w-full flex justify-center mt-auto py-4`}>
                                            <Pagination
                                                size="large"
                                                color="primary"
                                                count={paginationCount}
                                                defaultPage={1}
                                                page={currentPage}
                                                siblingCount={1}
                                                boundaryCount={2}
                                                onChange={changePaginationHandler}
                                            />
                                        </div>
                                    </div>
                            }

                            {/* Filtros de búsqueda */}
                            {
                                !loading &&
                                <div className="w-full overflow-y-scroll flex p-2 flex-col col-span-3 order-last border-l-2 border-light">
                                    <Accordion className="accordion-search-filter">
                                        <AccordionSummary
                                            expandIcon={<IonIcon icon={caretDown} className="text-[18px] text-black" />}
                                            aria-controls="panel1a-content"
                                            id="panel1a-header"
                                        >
                                            <p className="font-semibold text-mid">Fecha</p>
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            <div className="flex flex-col items-center gap-12">
                                                <div className="flex flex-col gap-2">
                                                    <p className="font-semibold">Fecha de inicio</p>
                                                    <IonDatetime
                                                        presentation="date"
                                                        value={formatDateToISODateString(startDate)}
                                                        max={formatDateToISODateString(today)}
                                                        onIonChange={(e: any) => {
                                                            const selectedDate = e.detail.value ? formatISODateStringToDate(e.detail.value, false) : new Date();
                                                            setStartDate(selectedDate ?? new Date());
                                                        }}
                                                    />
                                                </div>

                                                <div className="flex flex-col gap-2">
                                                    <p className="font-semibold">Fecha de fin</p>
                                                    <IonDatetime
                                                        presentation="date"
                                                        value={formatDateToISODateString(finishDate)}
                                                        max={formatDateToISODateString(today)}
                                                        onIonChange={(e: any) => {
                                                            const selectedDate = e.detail.value ? formatISODateStringToDate(e.detail.value, false) : new Date();
                                                            setFinishDate(selectedDate ?? new Date());
                                                        }}
                                                    />
                                                </div>

                                                <div className="w-full xl:w-1/2">
                                                    <SolidButton
                                                        text="Aplicar"
                                                        backgroundColor="primary"
                                                        disabled={false}
                                                        onClickHandler={() => {
                                                            const controller = new AbortController();
                                                            getBenchmarkDocs(controller.signal);
                                                            return () => controller.abort();
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </AccordionDetails>
                                    </Accordion>

                                    <Accordion className="accordion-search-filter">
                                        <AccordionSummary
                                            expandIcon={<IonIcon icon={caretDown} className="text-[18px] text-black" />}
                                            aria-controls="panel1a-content"
                                            id="panel1a-header"
                                        >
                                            <p className="font-semibold text-mid">Displays</p>
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            <div className="flex flex-col">
                                                {
                                                    searchFilters.user.length === 0 ?
                                                        <p className="font-semibold text-primary">No hay datos disponibles</p>
                                                        :
                                                        searchFilters.user.map((item: any) => {
                                                            return (
                                                                <FormControlLabel
                                                                    key={item.key}
                                                                    label={item.name + " " + item.lastName}
                                                                    control=
                                                                    {
                                                                        <Checkbox
                                                                            id={item.key}
                                                                            name={item.name + " " + item.lastName}
                                                                            value={item.key}
                                                                            checked={item.checked}
                                                                            onChange={() => changeSearchFilterHandler(item, "user")}
                                                                        />
                                                                    }
                                                                />
                                                            )
                                                        })
                                                }
                                            </div>
                                        </AccordionDetails>
                                    </Accordion>

                                    <Accordion className="accordion-search-filter">
                                        <AccordionSummary
                                            expandIcon={<IonIcon icon={caretDown} className="text-[18px] text-black" />}
                                            aria-controls="panel1a-content"
                                            id="panel1a-header"
                                        >
                                            <p className="font-semibold text-mid">Sucursales</p>
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            <div className="flex flex-col">
                                                {
                                                    searchFilters.sucursal.length === 0 ?
                                                        <p className="font-semibold text-primary">No hay datos disponibles</p>
                                                        :
                                                        searchFilters.sucursal.map((item: any) => {
                                                            return (
                                                                <FormControlLabel
                                                                    key={item.key}
                                                                    label={item.name}
                                                                    control=
                                                                    {
                                                                        <Checkbox
                                                                            id={item.key}
                                                                            name={item.name}
                                                                            value={item.key}
                                                                            checked={item.checked}
                                                                            onChange={() => changeSearchFilterHandler(item, "sucursal")}
                                                                        />
                                                                    }
                                                                />
                                                            )
                                                        }
                                                        )
                                                }
                                            </div>
                                        </AccordionDetails>
                                    </Accordion>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>

            {
                showImageModal &&
                <>
                    <ModalBackground />
                    <ImageVisualizer imageArray={imagesToShow} imageAlt="prueba" clickOutsideHandler={() => setShowImageModal(false)} />
                </>
            }
        </>
    )
}